import React, { useState } from 'react';
import { Settings, Triangle, ChevronDown, Menu, X } from 'lucide-react';
import type { BrandingConfig } from '../App';
interface NavbarProps {
  activePage: 'dashboard' | 'vulnerability' | 'settings' | 'scans' | 'bulletins' | 'teams';
  onNavigate: (page: 'dashboard' | 'vulnerability' | 'settings' | 'scans' | 'bulletins' | 'teams') => void;
  onSignOut: () => void;
  orgName?: string;
  branding?: BrandingConfig;
}
export function Navbar({
  activePage,
  onNavigate,
  onSignOut,
  orgName,
  branding
}: NavbarProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const brandColor = branding?.primaryColor || '#22c55e';
  const brandName = branding?.brandName || 'Astrolayb';
  const logoUrl = branding?.logoUrl || '';
  const isCustomBrand = branding?.brandName && branding.brandName !== 'Astrolayb';
  const navItems = [{
    label: 'Dashboard',
    page: 'dashboard' as const
  }, {
    label: 'Scans',
    page: 'scans' as const
  }, {
    label: 'Bulletins',
    page: 'bulletins' as const
  }, {
    label: 'Teams',
    page: 'teams' as const
  }];
  const handleNavigate = (page: NavbarProps['activePage']) => {
    onNavigate(page);
    setMobileMenuOpen(false);
  };
  return <nav className="border-b border-astro-border bg-astro-dark sticky top-0 z-40" data-id="element-489">
      <div className="h-14 flex items-center justify-between px-4 lg:px-6 max-w-[1440px] mx-auto" data-id="element-490">
        {/* Left: Logo + Nav */}
        <div className="flex items-center gap-6 min-w-0" data-id="element-491">
          <button onClick={() => handleNavigate('dashboard')} className="flex items-center gap-2 shrink-0" data-id="element-492">

            {logoUrl ? <img src={logoUrl} alt={brandName} className="h-7 max-w-[140px] object-contain" data-id="element-493" /> : <>
                <Triangle className="fill-current" style={{
              color: brandColor
            }} size={20} strokeWidth={2.5} data-id="element-494" />

                <span className="text-lg font-bold tracking-tight" style={{
              color: brandColor
            }} data-id="element-495">

                  {brandName}
                </span>
              </>}
          </button>

          <div className="hidden md:block w-px h-5 bg-astro-border shrink-0" data-id="element-496" />

          <div className="hidden md:flex items-center gap-0.5" data-id="element-497">
            {navItems.map(item => <button key={item.label} onClick={() => handleNavigate(item.page)} className={`px-3 py-1.5 text-[13px] font-medium rounded-md transition-colors ${item.page === activePage ? 'text-astro-green bg-astro-green-dim' : 'text-astro-text-secondary hover:text-white hover:bg-white/[0.04]'}`} data-id="element-498">

                {item.label}
              </button>)}
          </div>
        </div>

        {/* Right: Status + User + Mobile Toggle */}
        <div className="flex items-center gap-3 shrink-0" data-id="element-499">
          <div className="hidden lg:flex items-center gap-1.5 px-2.5 py-1 rounded-full" style={{
          backgroundColor: `${brandColor}15`
        }} data-id="element-500">

            <div className="w-1.5 h-1.5 rounded-full" style={{
            backgroundColor: brandColor,
            boxShadow: `0 0 4px ${brandColor}`
          }} data-id="element-501" />

            <span className="text-[11px] font-medium" style={{
            color: brandColor
          }} data-id="element-502">

              Alerts ON
            </span>
          </div>

          <div className="hidden sm:flex items-center gap-2 pl-2 border-l border-astro-border" data-id="element-503">
            <div className="w-7 h-7 rounded-full border border-astro-border flex items-center justify-center" style={{
            backgroundColor: `${brandColor}15`
          }} data-id="element-504">

              <span className="text-[11px] font-bold" style={{
              color: brandColor
            }} data-id="element-505">

                {orgName ? orgName.charAt(0).toUpperCase() : brandName.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="hidden xl:flex flex-col items-start leading-none" data-id="element-506">
              <span className="text-[13px] text-white font-medium" data-id="element-507">
                {orgName || 'avistardemo'}
              </span>
              {orgName && <span className="text-[10px] text-astro-text-secondary" data-id="element-508">
                  Owner
                </span>}
            </div>
          </div>

          <button onClick={() => handleNavigate('settings')} className={`p-1.5 border border-astro-border rounded-md transition-colors ${activePage === 'settings' ? 'text-astro-green bg-astro-green-dim border-astro-green/30' : 'text-astro-text-secondary hover:text-astro-green hover:bg-astro-green-dim'}`} aria-label="Settings" data-id="element-509">

            <Settings size={16} data-id="element-510" />
          </button>

          <button onClick={onSignOut} className="hidden sm:block px-3 py-1.5 text-[13px] font-medium text-astro-text-secondary border border-astro-border rounded-md hover:text-white hover:border-astro-text-secondary/30 transition-colors" data-id="element-511">

            Sign out
          </button>

          {/* Mobile menu toggle */}
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-1.5 text-astro-text-secondary hover:text-white transition-colors" aria-label="Toggle menu" data-id="element-512">

            {mobileMenuOpen ? <X size={20} data-id="element-513" /> : <Menu size={20} data-id="element-514" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Drawer */}
      {mobileMenuOpen && <div className="md:hidden border-t border-astro-border bg-astro-card px-4 py-3 space-y-1 animate-in slide-in-from-top-2 duration-150" data-id="element-515">
          {navItems.map(item => <button key={item.label} onClick={() => handleNavigate(item.page)} className={`w-full text-left px-3 py-2.5 text-sm font-medium rounded-md transition-colors ${item.page === activePage ? 'text-astro-green bg-astro-green-dim' : 'text-astro-text-secondary hover:text-white hover:bg-white/[0.04]'}`} data-id="element-516">

              {item.label}
            </button>)}
          <div className="pt-2 mt-2 border-t border-astro-border" data-id="element-517">
            <button onClick={() => {
          handleNavigate('settings');
        }} className={`w-full text-left px-3 py-2.5 text-sm font-medium rounded-md transition-colors ${activePage === 'settings' ? 'text-astro-green bg-astro-green-dim' : 'text-astro-text-secondary hover:text-white hover:bg-white/[0.04]'}`} data-id="element-518">

              Settings
            </button>
            <button onClick={() => {
          setMobileMenuOpen(false);
          onSignOut();
        }} className="w-full text-left px-3 py-2.5 text-sm font-medium text-astro-text-secondary rounded-md hover:text-white hover:bg-white/[0.04] transition-colors" data-id="element-519">

              Sign out
            </button>
          </div>
        </div>}
    </nav>;
}